import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Inbox, Bot, LayoutGrid, Star, ShieldCheck, Clock, Users, ArrowRight, CalendarCheck, FileText, CheckCircle2, Check } from 'lucide-react';
const BOOKING_URL = 'https://api.viridiaanalytics.com/widget/booking/hv5pIfS27zbJCMMGNnkJ';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png';
const HUBSPOT_BADGE = 'https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png';
const CHAMBER_BADGE = 'https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png';
function trackLead() {
  const w = window as any;
  if (typeof w.fbq === 'function') {
    w.fbq('track', 'Lead');
  }
}
function BookCTA({
  label = 'Book Your Free 15-Minute Call',
  full = false
}: {
  label?: string;
  full?: boolean;
}) {
  return <a href={BOOKING_URL} onClick={trackLead} target="_blank" rel="noopener noreferrer" className={`group inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-6 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-emerald-400 ${full ? 'w-full' : ''}`} data-id="element-0" data-mp="28sur4e8bzhhi">
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-1" data-mp="3rc9i4j7dc031" />
    </a>;
}
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="font-eyebrow inline-block text-[11px] font-semibold uppercase text-emerald-500" data-id="element-2" data-mp="82ohezguggq">
      {children}
    </div>;
}

/* ---------- Animated lifecycle pipeline ---------- */
const FLOW_SOURCES = ['Missed call', 'Yelp', 'Google', 'Facebook', 'Web form'];
const FLOW_STAGES = [{
  icon: Inbox,
  label: 'Lead captured',
  sub: 'Every call & message'
}, {
  icon: CalendarCheck,
  label: 'Booked',
  sub: 'On your calendar'
}, {
  icon: FileText,
  label: 'Invoice sent',
  sub: 'QuickBooks synced'
}, {
  icon: CheckCircle2,
  label: 'Job completed',
  sub: 'Tracked to done'
}, {
  icon: Star,
  label: 'Review requested',
  sub: 'Your reputation grows'
}];
function FlowNode({
  stage,
  state
}: {
  stage: (typeof FLOW_STAGES)[number];
  state: 'idle' | 'current' | 'done';
}) {
  const Icon = stage.icon;
  const done = state === 'done';
  const current = state === 'current';
  return <div className="flex w-28 flex-none flex-col items-center text-center sm:w-32" data-id="element-3" data-mp="d3wbp34vdary">
      <div className="relative" data-id="element-4" data-mp="864a1hefaaak">
        {current && <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-[vaPing_1.6s_ease-out_infinite]" data-id="element-5" data-mp="3gle9siuub3f6" />}
        <div className={`relative flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-500 ${done ? 'border-emerald-500 bg-emerald-500 text-black' : current ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-hairline2 bg-inkAlt text-neutral-600'}`} data-id="element-6" data-mp="3bmthr6pqrq1t">
          {done ? <Check className="h-5 w-5" strokeWidth={2.5} data-id="element-7" data-mp="2swlgyv9ilbos" /> : <Icon className="h-5 w-5" strokeWidth={2} data-id="element-8" data-mp="2stpjf81kwp0v" />}
        </div>
      </div>
      <div className={`mt-3 text-sm font-semibold transition-colors duration-500 ${done || current ? 'text-white' : 'text-neutral-500'}`} data-id="element-9" data-mp="18c6lbwc21eb7">{stage.label}</div>
      <div className={`mt-0.5 text-[11px] leading-tight transition-colors duration-500 ${done || current ? 'text-neutral-400' : 'text-neutral-600'}`} data-id="element-10" data-mp="yg3lix4odrrf">{stage.sub}</div>
    </div>;
}
function FlowConnector({
  filled
}: {
  filled: boolean;
}) {
  return <div className="flex items-center justify-center py-1 md:flex-1 md:px-1 md:py-0" data-id="element-11" data-mp="1r2698fw5snd1">
      <div className="relative h-7 w-px overflow-hidden rounded bg-hairline2 md:mt-6 md:h-px md:w-full" data-id="element-12" data-mp="1w2u0n2oav67s">
        <div className={`absolute inset-0 origin-top rounded bg-emerald-500 transition-transform duration-700 ease-out md:origin-left ${filled ? 'scale-y-100 md:scale-x-100' : 'scale-y-0 md:scale-x-0'} scale-x-100 md:scale-y-100`} data-id="element-13" data-mp="1i3b4z9codncd" />
      </div>
    </div>;
}
function LeadFlow() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setActive(FLOW_STAGES.length);
      return;
    }
    const id = setInterval(() => {
      setActive(a => a >= FLOW_STAGES.length ? 0 : a + 1);
    }, 1150);
    return () => clearInterval(id);
  }, []);
  const capturing = active === 0;
  return <div className="relative mt-10 overflow-hidden rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-10" data-id="element-14" data-mp="19v88un6cf3d1">
      <style data-id="element-15" data-mp="14v3sqy9ckanc">{`@keyframes vaPing{0%{transform:scale(1);opacity:.5}70%,100%{transform:scale(2);opacity:0}}`}</style>
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{
      background: 'radial-gradient(55% 60% at 50% 38%, rgba(15,176,125,0.12) 0%, rgba(10,10,10,0) 72%)'
    }} data-id="element-16" data-mp="vw8mjm6az0k1" />
      <div className="relative" data-id="element-17" data-mp="3klq0llaifg5f">
        <div className="font-eyebrow text-center text-[10px] font-semibold uppercase text-neutral-500" data-id="element-18" data-mp="3glxzturcyeam">Leads come in from</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-id="element-19" data-mp="1w5q4cfjjm5ah">
          {FLOW_SOURCES.map(s => <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-500 ${capturing ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-hairline2 bg-ink text-neutral-500'}`} data-id="element-20" data-mp="206a13i07f6w3">
              <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-500 ${capturing ? 'bg-emerald-400' : 'bg-neutral-600'}`} data-id="element-21" data-mp="2jthscgeg46rl" />
              {s}
            </span>)}
        </div>
        <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-b from-emerald-500/60 to-transparent" data-id="element-22" data-mp="2sv7h1egy0rlo" />
        <div className="mt-2 flex flex-col items-center md:flex-row md:items-start md:justify-center" data-id="element-23" data-mp="tid7f8xhqrp4">
          {FLOW_STAGES.map((stage, i) => {
          const state: 'idle' | 'current' | 'done' = active > i ? 'done' : active === i ? 'current' : 'idle';
          return <React.Fragment key={stage.label} data-id="element-24" data-mp="1wsr1xupe4b8f" data-mpi={i}>
                <FlowNode stage={stage} state={state} data-id="element-25" data-mp="1wu25puiu5izj" />
                {i < FLOW_STAGES.length - 1 && <FlowConnector filled={active > i} data-id="element-26" data-mp="2at1s0vivllsj" />}
              </React.Fragment>;
        })}
        </div>
        <div className="mt-8 text-center text-xs text-neutral-500" data-id="element-27" data-mp="25ruo57jut6r5">One system runs the whole job, start to finish. You just show up and do the work.</div>
      </div>
    </div>;
}
export function App() {
  return <div className="min-h-full w-full bg-ink text-neutral-100" data-id="element-28" data-mp="2fpiqojnpbijv">
      {/* HERO */}
      <header className="relative overflow-hidden border-b border-hairline" data-id="element-29" data-mp="2tkum6t9old1u">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{
        background: 'radial-gradient(60% 50% at 50% 0%, rgba(15,176,125,0.16) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-30" data-mp="2phojfvls2404" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-14 pt-10 text-center sm:pb-20 sm:pt-14" data-id="element-31" data-mp="2yiw3p9rgl4b7">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-8 w-auto sm:h-9" data-id="element-32" data-mp="3i4efx1t6b4gp" />
          <div className="mt-8" data-id="element-33" data-mp="3n27lptqkdwgg"><Eyebrow data-id="element-34" data-mp="3n0n2h09p9zxw">Phoenix Home-Service Owners</Eyebrow></div>
          <h1 className="mt-4 text-[2.1rem] font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl" data-id="element-35" data-mp="384fahds1vu82">
            Never miss a lead you already paid for.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-36" data-mp="2zclr7jl505es">
            The Never Miss A Job System answers every call, text, and Yelp lead in seconds and books
            the job for you, even when your hands are full. One system, one team, one bill.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-400" data-id="element-37" data-mp="2ud92etapgo9k">
            <ShieldCheck className="h-4 w-4" data-id="element-38" data-mp="2kfuvplel3dug" /> 14-Day Money-Back Guarantee
          </div>
          <div className="mt-7" data-id="element-39" data-mp="2khpbklb00fro"><BookCTA data-id="element-40" data-mp="30vx35tktntk7" /></div>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-41" data-mp="30uc53tzatshl">$1,197/month · month to month · no contract</p>
        </div>
      </header>

      {/* THE PROBLEM */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-42" data-mp="2qx7hdppt1oyo">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-43" data-mp="2qvcwucig3yv4">
          <Eyebrow data-id="element-44" data-mp="2lyw8pib8xki1">What a missed call actually costs you</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-45" data-mp="2hwj3cv0evols">
            The caller doesn't leave a voicemail. They just call the next name on Google.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-46" data-mp="286pvo53e3tmt">
            A busy shop misses 1 out of every 4 calls. At a few thousand dollars a job, that is tens
            of thousands walking out the door every month. Leads you already paid to generate.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-47" data-mp="1yad4pez8btvr">
            {["You're on a rooftop swapping a condenser unit and the phone buzzes in your pocket.", "You're under a sink mid-repair with wet hands and no way to grab it.", "You're driving between jobs and it goes straight to voicemail.", "You're mid-estimate with one customer while a second lead is calling right now."].map((line, i) => <div key={i} className="rounded-lg border border-hairline bg-ink p-5 text-sm leading-relaxed text-neutral-400" data-id="element-48" data-mp="nmkyli51jbqj" data-mpi={i}>{line}</div>)}
          </div>
        </div>
      </section>

      {/* THE FLOW */}
      <section className="border-b border-hairline" data-id="element-49" data-mp="3w4h7gnnzysfh">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-50" data-mp="3w2wakre7mxeg">
          <Eyebrow data-id="element-51" data-mp="4qbgmjr0q119">From missed call to five-star review</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-52" data-mp="4rmige0wkypq">
            One lead comes in. The system runs the entire job for you.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-53" data-mp="38d218jt3nqyy">
            Every call, text, and message from every platform lands in one place, then the system
            carries it the whole way, booked, invoiced, completed, and reviewed. Nothing slips.
          </p>
          <LeadFlow data-id="element-54" data-mp="2p5uffulwdze1" />
        </div>
      </section>

      {/* THE SYSTEM */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-55" data-mp="2u3ct1yav12sd">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-56" data-mp="1wgnfqsapj3r7">
          <Eyebrow data-id="element-57" data-mp="1wigp4fcpg22w">The Never Miss A Job System</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-58" data-mp="21exf7f3gsqim">
            One system that answers, texts back, and keeps every lead in one place.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-59" data-mp="2b61pi1i73n74">
            {[{
            icon: Bot,
            title: 'Instant response to every lead',
            body: 'AI receptionist or a Yelp and Google auto-response, answering every call, text, and message in seconds, 24/7, before they dial the next name.'
          }, {
            icon: Inbox,
            title: 'One unified inbox',
            body: 'Every call, text, Yelp, Google, and Facebook message lands in one place, not five apps.'
          }, {
            icon: LayoutGrid,
            title: 'Branded CRM + mobile app',
            body: 'Run the whole thing from your phone. It chases every lead until they book or say no.'
          }, {
            icon: Star,
            title: 'Reviews + QuickBooks-synced invoicing',
            body: 'Reputation and invoicing run in the background while you run the job. Google Business Profile set up and optimized.'
          }].map(({
            icon: Icon,
            title,
            body
          }, i) => <div key={i} className="rounded-lg border border-hairline2 bg-inkAlt p-6" data-id="element-60" data-mp="347t5sit1m3ry" data-mpi={i}>
                <Icon className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-61" data-mp="2z9hvk6yu5r97" />
                <h3 className="mt-4 text-base font-semibold text-white" data-id="element-62" data-mp="2z7ew6wbsmlrt">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-63" data-mp="2u9dcqaaggkft">{body}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="border-b border-hairline" data-id="element-64" data-mp="2flv799p2jlsc">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20" data-id="element-65" data-mp="2km9cyl376jn7">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10" data-id="element-66" data-mp="2kk6i9nr3o2c5">
            <ShieldCheck className="h-7 w-7 text-emerald-500" data-id="element-67" data-mp="2anlcv1b64p7s" />
          </div>
          <h2 className="mt-5 text-2xl font-extrabold leading-snug text-white sm:text-3xl" data-id="element-68" data-mp="2ye7f2byhrf9v">
            We respond to every missed call in your first 14 days, or your first month is 100% money back.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-neutral-400" data-id="element-69" data-mp="2pmfcenfplafu">
            We guarantee the one thing we fully control: missed-call response speed. Every response is
            timestamped and logged. If our system does not respond to every missed call within 5
            minutes during your first 14 days, your first month is refunded, no hassle. See full terms below.
          </p>
        </div>
      </section>

      {/* PROOF */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-70" data-mp="1am6pjkbv9a4c">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-71" data-mp="15ox3menorgl9">
          <Eyebrow data-id="element-72" data-mp="1kevx444aq3ue">Built by people who've run this before</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-73" data-mp="1kg70i2ttx9kz">
            Built by former Yelp reps who managed over $2 million a month in ad spend for
            home-service businesses.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-74" data-mp="mrds94dr9lvo">
            <div className="rounded-lg border border-hairline bg-ink p-6" data-id="element-75" data-mp="11nuy5g36e8au">
              <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-76" data-mp="11ls2affcdky7">Real client result · Phoenix landscaping company</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-77" data-mp="2d28iiayq05m4">284% more leads on the same ad spend, a 39.95% close rate on AI-booked estimates, and response time dropped from 4+ hours to instant.</p>
            </div>
            <div className="rounded-lg border border-hairline bg-ink p-6" data-id="element-78" data-mp="hoecfc7nh3dq">
              <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-79" data-mp="mmxuv2n1bx1c">Real client result · residential/commercial painting company</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-80" data-mp="vmkw9ofrl2ue">29 AI-booked estimates in 30 days, totaling $15.05k in closed-won revenue.</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-neutral-500" data-id="element-81" data-mp="10juvtwwvauvy">Real results for past clients, shown as proof the system works. Not a guarantee of the same numbers for every business.</p>
          <div className="mt-10 flex flex-wrap items-center gap-6 opacity-90" data-id="element-82" data-mp="1acae6vc2tlhd">
            <img src={HUBSPOT_BADGE} alt="HubSpot SEO certified" className="h-10 w-auto" data-id="element-83" data-mp="1ebspr0i0ck7j" />
            <img src={CHAMBER_BADGE} alt="Scottsdale Chamber of Commerce" className="h-10 w-auto" data-id="element-84" data-mp="1jd898o4jeint" />
          </div>
        </div>
      </section>

      {/* OFFER / PRICING */}
      <section className="border-b border-hairline" data-id="element-85" data-mp="1yfq7e07mbv21">
        <div className="mx-auto max-w-2xl px-5 py-14 text-center sm:py-20" data-id="element-86" data-mp="1ygi4jfgv60uf">
          <Eyebrow data-id="element-87" data-mp="1tfca9d7k25is">The offer</Eyebrow>
          <h2 className="mt-3 text-3xl font-extrabold text-white sm:text-4xl" data-id="element-88" data-mp="1tigjiozvnrgb">$1,197<span className="text-lg font-semibold text-neutral-400" data-id="element-89" data-mp="1ohj1ccevaxet">/month</span></h2>
          <p className="mt-2 text-sm font-medium text-emerald-500" data-id="element-90" data-mp="1kgqsvldtkwjl">Month to month. No contract. Cancel anytime.</p>
          <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-91" data-mp="2h4wt7w3pl2s2">
            The whole system, one bill, one team to call, backed by the 14-day money-back guarantee.
            Less than the cost of two missed jobs a month.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-92" data-mp="23f337aggwcgr">
            <span className="inline-flex items-center gap-1.5" data-id="element-93" data-mp="2zwzfbmz4db9p"><Clock className="h-3.5 w-3.5 text-emerald-500" data-id="element-94" data-mp="2v5hecyx3jaoq" /> Live in days, not months</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-95" data-mp="2v3mt1jhwxgk4"><Users className="h-3.5 w-3.5 text-emerald-500" data-id="element-96" data-mp="t98h6pa23bhb" /> Only 5 new businesses onboarded per month</span>
          </div>
          <div className="mt-8 flex justify-center" data-id="element-97" data-mp="y68lovr9c66b"><BookCTA data-id="element-98" data-mp="jidwvqgdelyi" /></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-99" data-mp="oh797fjx86x3">
        <div className="mx-auto max-w-2xl px-5 py-16 text-center sm:py-24" data-id="element-100" data-mp="ofmffs5fks0a">
          <h2 className="text-2xl font-extrabold leading-snug text-white sm:text-3xl" data-id="element-101" data-mp="agebklybovpr">Stop losing jobs to voicemail.</h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-102" data-mp="fi3ne3tdddew">
            Book a free 15-minute call. We'll run a quick Missed-Call Audit and show you, in dollars,
            exactly what you're losing right now.
          </p>
          <div className="mt-8 flex justify-center" data-id="element-103" data-mp="1uwreidkj6563"><BookCTA data-id="element-104" data-mp="1uxsr4mh72zpx" /></div>
        </div>
      </section>

      {/* TERMS */}
      <section id="terms" className="border-b border-hairline" data-id="element-105" data-mp="1zvb1mhav1n01">
        <div className="mx-auto max-w-3xl px-5 py-12" data-id="element-106" data-mp="3keekiqwwd2ad">
          <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-107" data-mp="3key040z2rel8">Guarantee terms &amp; conditions</div>
          <div className="mt-3 space-y-2 text-[11px] leading-relaxed text-neutral-500" data-id="element-108" data-mp="3fdh2qffsua9e">
            <p data-id="element-109" data-mp="3u6aup0c9ciuy">The 14-day money-back guarantee applies to the first month only and to missed-call response speed only. It guarantees that Viridia's system sends a response to every missed call it receives within 5 minutes during the client's first 14 days after go-live. Response times are automatically timestamped and logged; those logs are the sole record for any guarantee claim.</p>
            <p data-id="element-110" data-mp="gwwn7der3do7">The guarantee covers missed-call response and delivery, which Viridia controls. It does not guarantee booked jobs, revenue, close rate, or number of customers, which depend on the client's own pricing, sales, and market. The 14-day window begins at go-live, once the system is active and the client has completed required onboarding.</p>
            <p data-id="element-111" data-mp="1rxc0zrsv82wk">The guarantee does not apply if the client has not completed onboarding or synced their calendar or granted required access, disconnects or revokes their phone, Yelp, Google, or CRM integrations, or pauses, disables, or routes calls outside the system. One claim per client; refund of the first month's fee is the sole and entire remedy. Any one-time setup fee is non-refundable.</p>
            <p data-id="element-112" data-mp="18yg5md59tih5">Service is month to month; cancel anytime with 7 days' notice. The system relies on third-party platforms (the client's phone carrier, Yelp, Google, Meta, and workflow tools); Viridia is not responsible for their outages or policy changes, and the guarantee is contingent on those integrations remaining connected. Client results shown are real past-client outcomes and are not a guarantee of the same results.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10" data-id="element-113" data-mp="2z9ukfjae5oul">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center" data-id="element-114" data-mp="2z8sw4d4d75ux">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-6 w-auto opacity-80" data-id="element-115" data-mp="nlpdv2fted3n" />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-116" data-mp="ioyxsf40fhi8">
            <span className="inline-flex items-center gap-1.5" data-id="element-117" data-mp="dvwceu9dzed4"><Phone className="h-3.5 w-3.5" data-id="element-118" data-mp="8y4b59muqfw9" /> (623) 300-0708</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-119" data-mp="8xul5m1t48qk"><Mail className="h-3.5 w-3.5" data-id="element-120" data-mp="3zjchen9049x" /> info@viridiaanalytics.com</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-121" data-mp="3w2tzbkfbv2de"><MapPin className="h-3.5 w-3.5" data-id="element-122" data-mp="3w210r6qivj6c" /> Phoenix, AZ</span>
          </div>
          <a href="https://viridiaanalytics.com" target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-600 underline decoration-neutral-800 underline-offset-4 hover:text-neutral-400" data-id="element-123" data-mp="3r2xvbl6mraxu">See the full Viridia Analytics site</a>
        </div>
      </footer>
    </div>;
}